let makerjs = require('makerjs')

import { layerNames } from '@/js/cad/CadLayerNaming.js'
import CadTextUtils from '@/js/cad/CadTextUtils.js'

export default class CoverPanel {
  constructor(panel, font, region) {
    this.panel = panel
    this.font = font
    this.panelCornerRadius = 2
    this.trimAmount = 3
    this.models = {}
    this.labelInset = 15
    this.lacquerLineEdgeInset = 130
    this.lacquerLineSideInset = 100
    this.minWidthForLacquer = 400
    this.lacquerCrossSize = 25

    // lighting data
    this.lightingPadding = 1
    this.lightingDepth = 21
    this.lightingHeight = 15
    this.lightingRebateCornerPoke = 1.5
    this.lightCableCoverWidth = 10
    this.lightCableWidth = 5
    this.lightCableCoverThickness = 6

    // v-plinth data
    this.vPlinthGrooveHeight = 8
    this.numOfVPlinthGrooves = 3
    this.vPlinthGrooveSpacing = 20

    this.height = panel.type == 'filler' ? panel.h - this.trimAmount : panel.h
    this.width = panel.w

    this.models.panel = new makerjs.models.RoundRectangle(panel.w, this.height, this.panelCornerRadius)
    this.models.panel.layer = layerNames.cutout.label

    let label
    switch (panel.type) {
      case 'end-b':
      case 'end-w':
        label = 'CP' + panel.itemNum
        break
      case 'end-b-l':
      case 'end-w-l':
        label = 'CPL' + panel.itemNum
        break
      case 'end-b-r':
      case 'end-w-r':
        label = 'CPR' + panel.itemNum
        break
      case 'island':
        label = 'ISL' + panel.itemNum
        break
      case 'v-plinth':
        label = 'VENT'
        break
      case 'plinth':
        label = (panel.w == 2400 || panel.w == 3000) && !panel.material.hasGrain ? null : 'PLINTH'
        break
      case 'filler':
        label = 'FP' + panel.itemNum
        break
      case 'back':
        label = 'BK' + panel.itemNum
        break
      case 'top':
        label = 'TP' + panel.itemNum
        break
      case 'underside':
        label = 'UP' + panel.itemNum
        break
      case 'custom':
        label = 'C' + panel.itemNum
        break
      case 'shelf':
        label = null
        break
      default:
        console.log('No label for cover panel type', panel.type)
        label = panel.itemNum.toString()
    }

    // Append edge treatment suffix if edges are selected
    if (label && panel.edges && panel.edgeMode) {
      const hasSelectedEdge = panel.edges.top || panel.edges.bottom || panel.edges.left || panel.edges.right
      if (hasSelectedEdge) {
        label += ' - EB'
      }
    }

    if (label) {
      let text = CadTextUtils.createEngravingText(font, label, 8)
      this.models.snazzyText = makerjs.model.mirror(text, true, false)
      this.models.snazzyText.layer =
        panel.doubleSided || panel.type === 'top' || panel.type === 'underside'
          ? layerNames.notes.label
          : layerNames.engrave.label

      let textMeasurements = makerjs.measure.modelExtents(this.models.snazzyText)
      makerjs.model.moveRelative(this.models.snazzyText, [-textMeasurements.center[0], -textMeasurements.center[1]])
      if (panel.type == 'underside' || panel.type == 'top') {
        // put engraving towards cable and 50mm off edge
        this.models.snazzyText = makerjs.model.rotate(this.models.snazzyText, 180)
        makerjs.model.moveRelative(this.models.snazzyText, [0, this.panel.h - 70])
      }

      !panel.vent
        ? makerjs.model.moveRelative(this.models.snazzyText, [panel.w / 2, 20])
        : panel.ventRef === 'r'
        ? makerjs.model.moveRelative(this.models.snazzyText, [(panel.w - panel.vent - panel.ventOffset) / 2, 20])
        : makerjs.model.moveRelative(this.models.snazzyText, [
            panel.w - (panel.w - panel.vent - panel.ventOffset) / 2,
            20
          ])

      // Store text dimensions for edge treatment engraving
      this.snazzyTextWidth = textMeasurements.width
      this.snazzyTextHeight = textMeasurements.height
    }

    // Add edge treatment engraving (EB/F with arrows)
    this.addEdgeTreatmentEngraving()

    if (region !== 'us' && panel.doubleSided !== true && panel.material?.hasGrain) {
      // When edgeMode is set and edges are selected, add lacquer guides for selected edges
      const hasSelectedEdge =
        panel.edgeMode &&
        panel.edges &&
        (panel.edges.top || panel.edges.bottom || panel.edges.left || panel.edges.right)

      const smallHorizontal =
        (panel.type === 'underside' || panel.type === 'top') && this.height < this.minWidthForLacquer

      if (smallHorizontal) {
        // no lacquer guide line
      } else if (hasSelectedEdge) {
        this.addSelectedEdgeLacquerGuides()
      } else {
        switch (panel.type) {
          case 'end-b':
          case 'end-w':
          case 'island':
            if (this.width > this.minWidthForLacquer) this.addVerticalLacquerGuides()
            break
          case 'top':
          case 'underside':
            this.addSingleHorizontalLacquerGuide('top')
            break
          case 'filler':
          case 'back':
            this.addNoLacquerMark()
            break
          default:
        }
      }

      // Always add orientation-specific single line for L/R end panels
      switch (panel.type) {
        case 'end-b-l':
        case 'end-w-l':
          if (this.width > this.minWidthForLacquer) this.addSingleVerticalLacquerGuide('left')
          break
        case 'end-b-r':
        case 'end-w-r':
          if (this.width > this.minWidthForLacquer) this.addSingleVerticalLacquerGuide('right')
          break
      }
    }

    if (panel.lighting) {
      this.createLighting()
    }

    if (panel.vent) {
      this.createVent()
    }

    // rotate to vertical orientation for Plinths, Undersides and Top Panels
    if (['v-plinth', 'plinth', 'top', 'underside', 'shelf'].includes(panel.type)) {
      for (let key in this.models) {
        this.models[key] = makerjs.model.rotate(this.models[key], 90)
        this.models[key] = makerjs.model.moveRelative(this.models[key], [panel.h, 0])
      }
    }
  }

  addVerticalLacquerGuides() {
    const paths = {}

    const hasSelectedVerticalEdge =
      this.panel.edgeMode && this.panel.edges && (this.panel.edges.left || this.panel.edges.right)

    if (hasSelectedVerticalEdge) {
      // Inverted mapping for flip side: dialog left edge → rightEdge line
      if (this.panel.edges.left) {
        paths.rightEdge = {
          type: 'line',
          origin: [this.lacquerLineEdgeInset, this.lacquerLineSideInset],
          end: [this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
        }
      }
      // Inverted mapping for flip side: dialog right edge → leftEdge line
      if (this.panel.edges.right) {
        paths.leftEdge = {
          type: 'line',
          origin: [this.width - this.lacquerLineEdgeInset, this.lacquerLineSideInset],
          end: [this.width - this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
        }
      }
    } else {
      // No edges selected - add both lines (default behavior)
      paths.rightEdge = {
        type: 'line',
        origin: [this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
      paths.leftEdge = {
        type: 'line',
        origin: [this.width - this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.width - this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
    }

    if (Object.keys(paths).length > 0) {
      this.models.lacquerLine = { paths }
      this.models.lacquerLine.layer = layerNames.cpEngrave.label
    }
  }

  addSingleVerticalLacquerGuide(side) {
    const paths = {}

    if (side === 'right') {
      paths.rightEdge = {
        type: 'line',
        origin: [this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
    } else {
      paths.leftEdge = {
        type: 'line',
        origin: [this.width - this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.width - this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
    }

    if (this.models.lacquerLine?.paths) {
      Object.assign(this.models.lacquerLine.paths, paths)
    } else {
      this.models.lacquerLine = { paths }
      this.models.lacquerLine.layer = layerNames.cpEngrave.label
    }
  }

  addSingleHorizontalLacquerGuide(side) {
    const paths = {}

    if (side === 'top') {
      paths.topEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, this.lacquerLineEdgeInset],
        end: [this.width - this.lacquerLineSideInset, this.lacquerLineEdgeInset]
      }
    } else {
      paths.bottomEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset],
        end: [this.width - this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset]
      }
    }

    this.models.lacquerLine = { paths }
    this.models.lacquerLine.layer = layerNames.cpEngrave.label
  }

  addHorizontalLacquerGuides() {
    const paths = {}

    const hasSelectedHorizontalEdge =
      this.panel.edgeMode && this.panel.edges && (this.panel.edges.top || this.panel.edges.bottom)

    if (hasSelectedHorizontalEdge) {
      // Inverted mapping for flip side: dialog bottom edge → topEdge line
      if (this.panel.edges.bottom) {
        paths.topEdge = {
          type: 'line',
          origin: [this.lacquerLineSideInset, this.lacquerLineEdgeInset],
          end: [this.width - this.lacquerLineSideInset, this.lacquerLineEdgeInset]
        }
      }
      // Inverted mapping for flip side: dialog top edge → bottomEdge line
      if (this.panel.edges.top) {
        paths.bottomEdge = {
          type: 'line',
          origin: [this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset],
          end: [this.width - this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset]
        }
      }
    } else {
      // No edges selected - add both lines (default behavior)
      paths.topEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, this.lacquerLineEdgeInset],
        end: [this.width - this.lacquerLineSideInset, this.lacquerLineEdgeInset]
      }
      paths.bottomEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset],
        end: [this.width - this.lacquerLineSideInset, this.height - this.lacquerLineEdgeInset]
      }
    }

    if (Object.keys(paths).length > 0) {
      // Merge with existing lacquerLine paths if they exist
      if (this.models.lacquerLine?.paths) {
        Object.assign(this.models.lacquerLine.paths, paths)
      } else {
        this.models.lacquerLine = { paths }
        this.models.lacquerLine.layer = layerNames.cpEngrave.label
      }
    }
  }

  addSelectedEdgeLacquerGuides() {
    const paths = {}

    // Only add guides for edges that are explicitly true
    if (this.panel.edges.left) {
      paths.rightEdge = {
        // inverted for flip side
        type: 'line',
        origin: [this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
    }
    if (this.panel.edges.right) {
      paths.leftEdge = {
        // inverted for flip side
        type: 'line',
        origin: [this.width - this.lacquerLineEdgeInset, this.lacquerLineSideInset],
        end: [this.width - this.lacquerLineEdgeInset, this.height - this.lacquerLineSideInset]
      }
    }
    const horizontalLabelFlip = this.panel.type === 'top' || this.panel.type === 'underside'

    if (this.panel.edges.bottom) {
      const y = horizontalLabelFlip ? this.height - this.lacquerLineEdgeInset : this.lacquerLineEdgeInset
      paths.bottomSelectedEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, y],
        end: [this.width - this.lacquerLineSideInset, y]
      }
    }
    if (this.panel.edges.top) {
      const y = horizontalLabelFlip ? this.lacquerLineEdgeInset : this.height - this.lacquerLineEdgeInset
      paths.topSelectedEdge = {
        type: 'line',
        origin: [this.lacquerLineSideInset, y],
        end: [this.width - this.lacquerLineSideInset, y]
      }
    }

    if (Object.keys(paths).length > 0) {
      this.models.lacquerLine = { paths }
      this.models.lacquerLine.layer = layerNames.cpEngrave.label
    }
  }

  addNoLacquerMark() {
    this.models.lacquerLine = {
      paths: {
        firstCross: {
          type: 'line',
          origin: [-this.lacquerCrossSize / 2, -this.lacquerCrossSize / 2],
          end: [this.lacquerCrossSize / 2, this.lacquerCrossSize / 2]
        },
        secondCross: {
          type: 'line',
          origin: [-this.lacquerCrossSize / 2, this.lacquerCrossSize / 2],
          end: [this.lacquerCrossSize / 2, -this.lacquerCrossSize / 2]
        }
      }
    }
    makerjs.model.moveRelative(this.models.lacquerLine, [this.width / 2, this.height / 2])
    this.models.lacquerLine.layer = layerNames.cpEngrave.label
  }

  createLighting() {
    const lightingWidth = this.panel.lighting + this.lightingPadding
    const lightingOffsetX =
      this.panel.lightingRef == 'r'
        ? this.panel.lightingOffset
        : this.panel.lightingRef == 'l'
        ? this.width - this.panel.lightingOffset - lightingWidth
        : (this.width - lightingWidth) / 2

    this.models.lighting = { models: {} }
    this.models.lighting.models.rebate = new makerjs.models.Rectangle(lightingWidth, this.lightingDepth)
    this.models.lighting.models.rebate.layer = layerNames.mittledGroove.label

    this.models.lighting.models.lines = {
      layer: layerNames.mittledCorner.label,
      paths: {
        line1: {
          type: 'line',
          origin: [this.lightingRebateCornerPoke, this.lightingRebateCornerPoke + 5],
          end: [this.lightingRebateCornerPoke, this.lightingRebateCornerPoke]
        },
        line2: {
          type: 'line',
          origin: [this.lightingRebateCornerPoke, this.lightingRebateCornerPoke],
          end: [this.lightingRebateCornerPoke + 5, this.lightingRebateCornerPoke]
        },
        line3: {
          type: 'line',
          origin: [-this.lightingRebateCornerPoke + lightingWidth - 5, this.lightingRebateCornerPoke],
          end: [-this.lightingRebateCornerPoke + lightingWidth, this.lightingRebateCornerPoke]
        },
        line4: {
          type: 'line',
          origin: [-this.lightingRebateCornerPoke + lightingWidth, this.lightingRebateCornerPoke],
          end: [-this.lightingRebateCornerPoke + lightingWidth, this.lightingRebateCornerPoke + 5]
        },
        line5: {
          type: 'line',
          origin: [
            -this.lightingRebateCornerPoke + lightingWidth,
            -this.lightingRebateCornerPoke + this.lightingDepth - 5
          ],
          end: [-this.lightingRebateCornerPoke + lightingWidth, -this.lightingRebateCornerPoke + this.lightingDepth]
        },
        line6: {
          type: 'line',
          origin: [-this.lightingRebateCornerPoke + lightingWidth, -this.lightingRebateCornerPoke + this.lightingDepth],
          end: [-this.lightingRebateCornerPoke + lightingWidth - 5, -this.lightingRebateCornerPoke + this.lightingDepth]
        },
        line7: {
          type: 'line',
          origin: [this.lightingRebateCornerPoke + 5, -this.lightingRebateCornerPoke + this.lightingDepth],
          end: [this.lightingRebateCornerPoke, -this.lightingRebateCornerPoke + this.lightingDepth]
        },
        line8: {
          type: 'line',
          origin: [this.lightingRebateCornerPoke, -this.lightingRebateCornerPoke + this.lightingDepth],
          end: [this.lightingRebateCornerPoke, -this.lightingRebateCornerPoke + this.lightingDepth - 5]
        }
      }
    }

    makerjs.model.move(this.models.lighting, [lightingOffsetX, this.height / 2 - this.lightingDepth / 2])

    // Cable housing
    let xPos = this.panel.lightingRef == 'l' ? this.lightCableWidth : lightingWidth

    // Channel
    this.models.lighting.models['cable-channel'] = new makerjs.models.RoundRectangle(
      this.lightCableWidth,
      this.height / 2 + this.lightCableWidth / 2 + 8,
      this.lightCableWidth / 2
    )
    makerjs.model.move(this.models.lighting.models['cable-channel'], [
      xPos - this.lightCableWidth,
      this.lightCableWidth / 2
    ])
    this.models.lighting.models['cable-channel'].layer = layerNames.cableChannelGroove.label

    // Channel plug boxes
    let boxPos = this.panel.lightingRef == 'l' ? xPos - 5 - 1 : xPos - 8 + 1
    this.models.lighting.models['cable-channel-plug-box-front'] = new makerjs.models.Rectangle(8, 15.5)
    makerjs.model.move(this.models.lighting.models['cable-channel-plug-box-front'], [boxPos, this.lightingDepth - 1])
    ;('cable-channel-plug-box-front')
    this.models.lighting.models['cable-channel-plug-box-front'].layer = layerNames.cableChannelGroove.label

    //
    this.models.lighting.models['cable-channel-plug-box-back'] = new makerjs.models.Rectangle(8, 15.5)
    makerjs.model.move(this.models.lighting.models['cable-channel-plug-box-back'], [boxPos, this.height / 2 - 4])
    ;('cable-channel-plug-box-back')
    this.models.lighting.models['cable-channel-plug-box-back'].layer = layerNames.cableChannelGroove.label

    // add Cover Channel & Cover Pieces for shelves ONLY
    if (this.panel.type === 'shelf') {
      // Cover Channel
      this.models.lighting.models['cable-cover-channe'] = new makerjs.models.RoundRectangle(
        this.lightCableCoverWidth,
        this.height / 2 + this.lightCableWidth + this.lightCableCoverWidth + 2,
        this.lightCableCoverWidth / 2
      )
      makerjs.model.move(this.models.lighting.models['cable-cover-channe'], [
        xPos - this.lightCableCoverWidth + (this.lightCableCoverWidth - this.lightCableWidth) / 2,
        0
      ])
      this.models.lighting.models['cable-cover-channe'].layer = layerNames.cableCoverGroove.label

      // Cover Piece
      const coverRect = new makerjs.models.Rectangle(
        this.lightCableCoverWidth,
        this.height / 2 + this.lightingDepth / 2 - this.lightCableCoverWidth / 2
      )
      const coverCirc = new makerjs.models.Ellipse(this.lightCableCoverWidth / 2, this.lightCableCoverWidth / 2)
      makerjs.model.move(coverCirc, [5, 0])

      this.models['cable-cover'] = makerjs.model.combine(coverRect, coverCirc, false, true, false, true)
      // this.models['cable-cover'] = makerjs.model.distort(this.models['cable-cover'], 1.05, 1)

      this.models['cable-cover'] = makerjs.layout.cloneToRow(this.models['cable-cover'], 2, 6)
      makerjs.model.move(this.models['cable-cover'], [-100, this.height + 100])

      this.models['cable-cover'].layer = layerNames.cableCoverCutout.label

      // cover piece mill
      const millRect = new makerjs.models.Rectangle(
        this.lightCableCoverWidth * 2 + 6 + 20,
        this.height / 2 + this.lightingDepth / 2 + 8 + 20
      )
      makerjs.model.move(millRect, [
        -(100 - this.lightCableCoverWidth + 20.5),
        this.height - 10 - this.lightCableCoverWidth / 2 + 100
      ])
      this.models['mill'] = millRect
      this.models['mill'].layer = layerNames.millCableCover.label
    }

    // add a mega warning
    let text = CadTextUtils.createEngravingText(
      this.font,
      'Cable + engrave on back side. Light on (good + flipped) face side.',
      80
    )
    this.models.warningText = makerjs.model.mirror(text, true, false)
    this.models.warningText.layer = layerNames.warning.label

    let textMeasurements = makerjs.measure.modelExtents(this.models.warningText)
    makerjs.model.moveRelative(this.models.warningText, [
      -textMeasurements.center[0] * 2,
      -textMeasurements.center[1] + 800
    ])
  }

  createVent() {
    const ventStartX =
      this.panel.ventRef === 'r'
        ? this.width - this.panel.ventOffset - this.vPlinthGrooveHeight / 2
        : this.panel.ventOffset + this.vPlinthGrooveHeight / 2
    const ventEndX =
      this.panel.ventRef === 'r'
        ? this.width - this.panel.ventOffset - this.panel.vent + this.vPlinthGrooveHeight / 2
        : this.panel.ventOffset + this.panel.vent - this.vPlinthGrooveHeight / 2
    const ventOffsetY = (this.height - (this.numOfVPlinthGrooves - 1) * this.vPlinthGrooveSpacing) / 2
    const botVentGrooveY = ventOffsetY
    const midVentGrooveY = ventOffsetY + this.vPlinthGrooveSpacing
    const topVentGrooveY = ventOffsetY + 2 * this.vPlinthGrooveSpacing
    this.models.vent = { models: {} }
    this.models.vent.models.lines = {
      layer: layerNames.ventLine.label,
      paths: {
        line1: {
          type: 'line',
          origin: [ventStartX, botVentGrooveY],
          end: [ventEndX, botVentGrooveY]
        },
        line2: {
          type: 'line',
          origin: [ventStartX, midVentGrooveY],
          end: [ventEndX, midVentGrooveY]
        },
        line3: {
          type: 'line',
          origin: [ventStartX, topVentGrooveY],
          end: [ventEndX, topVentGrooveY]
        }
      }
    }
  }

  addEdgeTreatmentEngraving() {
    if (!this.panel.edges || !this.panel.edgeMode) return

    const edges = this.panel.edges
    // Check if any edges are selected
    if (!edges.top && !edges.bottom && !edges.left && !edges.right) return

    // Need text dimensions to position circles from edges
    if (!this.snazzyTextWidth || !this.snazzyTextHeight) return

    const circleRadius = 1 // Small circle radius in mm
    const circleGap = 5 // Gap between text edge and circle

    // Main label is at y=20, centered at panel.w/2
    const labelX = this.panel.w / 2
    const labelY = 20

    // Calculate text edges
    const textHalfWidth = this.snazzyTextWidth / 2
    const textHalfHeight = this.snazzyTextHeight / 2

    const layer = this.panel.doubleSided ? layerNames.notes.label : layerNames.engrave.label

    // Top edge (dialog) → circle above the label (from top edge of text)
    if (edges.top) {
      this.models.edgeCircleTop = {
        layer: layer,
        paths: {
          circle: {
            type: 'circle',
            origin: [labelX, labelY + textHalfHeight + circleGap],
            radius: circleRadius
          }
        }
      }
    }

    // Bottom edge (dialog) → circle below the label (from bottom edge of text)
    if (edges.bottom) {
      this.models.edgeCircleBottom = {
        layer: layer,
        paths: {
          circle: {
            type: 'circle',
            origin: [labelX, labelY - textHalfHeight - circleGap],
            radius: circleRadius
          }
        }
      }
    }

    // Right edge in dialog (face-on) → circle to the right in CAD (from right edge of text)
    if (edges.right) {
      this.models.edgeCircleForRight = {
        layer: layer,
        paths: {
          circle: {
            type: 'circle',
            origin: [labelX + textHalfWidth + circleGap, labelY],
            radius: circleRadius
          }
        }
      }
    }

    // Left edge in dialog (face-on) → circle to the left in CAD (from left edge of text)
    if (edges.left) {
      this.models.edgeCircleForLeft = {
        layer: layer,
        paths: {
          circle: {
            type: 'circle',
            origin: [labelX - textHalfWidth - circleGap, labelY],
            radius: circleRadius
          }
        }
      }
    }
  }
}
